import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import React from "react";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import WifiOffOutlinedIcon from "@mui/icons-material/WifiOffOutlined";
import lsLogo from "../../assets/LegalStreamLogo.avif";

const Logo = () => (
    <Box sx={{ display: "inline-flex", alignItems: "center", mb: 3 }}>
        <img src={lsLogo} alt="LegalStream" style={{ height: 72 }} />
    </Box>
);

const StatusPill = () => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                backgroundColor: alpha(theme.palette.warning.main, 0.12),
                border: `0.5px solid ${theme.palette.warning.light}`,
                borderRadius: "999px",
                px: "14px",
                py: "5px",
                mb: 3,
            }}
        >
            <Box
                sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: "warning.main",
                    "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.25 },
                    },
                    animation: "pulse 1.8s ease-in-out infinite",
                }}
            />
            <Typography sx={{ fontSize: 13, color: "warning.dark" }}>
                Temporarily Unreachable
            </Typography>
        </Box>
    );
};

const steps = [
    {
        icon: <RefreshOutlinedIcon sx={{ fontSize: 18, color: "primary.main", mt: "1px", flexShrink: 0 }} />,
        text: "Refresh the page in a few minutes - the issue is often resolved quickly.",
    },
    {
        icon: <WifiOffOutlinedIcon sx={{ fontSize: 18, color: "primary.main", mt: "1px", flexShrink: 0 }} />,
        text: "Check your internet connection if the problem persists.",
    },
];

const AppDownPage = () => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "primary.main",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
            }}
        >
            <Box
                sx={{
                    backgroundColor: "background.paper",
                    borderRadius: 4,
                    p: { xs: "2rem 1.5rem", sm: "2.5rem 2.5rem 2rem" },
                    maxWidth: 500,
                    width: "100%",
                    textAlign: "center",
                }}
            >
                {/* Logo */}
                <Logo />

                {/* Cloud-off icon */}
                <Box
                    sx={{
                        width: 68,
                        height: 68,
                        borderRadius: "50%",
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                    }}
                >
                    <CloudOffOutlinedIcon sx={{ fontSize: 30, color: "primary.main" }} />
                </Box>

                {/* Title */}
                <Typography variant="h6" sx={{ fontWeight: 500, color: "primary.dark", mb: 0.5 }}>
                    We'll be right back
                </Typography>

                {/* Pulsing status pill */}
                <StatusPill />

                {/* Body copy */}
                <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", lineHeight: 1.75, mb: 2.5 }}
                >
                    We're working to restore full access as soon as possible. No action is needed on your end.
                </Typography>

                {/* While you wait steps */}
                <Box
                    sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderRadius: 2.5,
                        p: "1.25rem 1.5rem",
                        textAlign: "left",
                        mb: 2.5,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            fontWeight: 500,
                            color: "primary.main",
                            mb: 1.25,
                        }}
                    >
                        While you wait
                    </Typography>

                    {steps.map((step, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "10px",
                                mb: i < steps.length - 1 ? 1 : 0,
                            }}
                        >
                            {step.icon}
                            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                                {step.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: "0.5px solid", borderColor: "divider", mb: 1.5 }} />

                {/* Footer */}
                <Typography variant="caption" color="text.disabled">
                    © {new Date().getFullYear()} LegalStream &nbsp;·&nbsp;
                </Typography>

                {/* Error reference code */}
                <Box sx={{ mt: 1 }}>
                    <Typography
                        component="code"
                        sx={{
                            fontSize: 11,
                            color: "text.disabled",
                            backgroundColor: "background.default",
                            borderRadius: 0.5,
                            px: "7px",
                            py: "2px",
                            fontFamily: "monospace",
                        }}
                    >
                        ERR_SERVICE_UNAVAILABLE
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default AppDownPage;